/* Premium Product Page — Gallery, Accordion, Anchor Nav, Add-on Cart, Variant Sync */

/* ── Gallery Web Component ── */
class PremiumGallery extends HTMLElement {
  connectedCallback() {
    this.thumbs = this.querySelectorAll('.premium-gallery__thumb');
    this.slides = this.querySelectorAll('.premium-gallery__slide');

    this.thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => this.setActive(thumb.dataset.mediaId));
    });
  }

  setActive(mediaId) {
    this.thumbs.forEach(t => t.classList.toggle('is-active', t.dataset.mediaId === mediaId));
    this.slides.forEach(s => s.classList.toggle('is-active', s.dataset.mediaId === mediaId));

    // Scroll active thumb into view
    const activeThumb = this.querySelector(`.premium-gallery__thumb[data-media-id="${mediaId}"]`);
    if (activeThumb) activeThumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
  }
}

customElements.define('premium-gallery', PremiumGallery);

/* ── Accordion ── */
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.premium-accordion__trigger').forEach(trigger => {
    const item = trigger.closest('.premium-accordion__item');
    trigger.addEventListener('click', () => {
      const isOpen = item.classList.contains('is-open');
      // Close all (comment this block out to allow multiple open)
      item.closest('.premium-accordion').querySelectorAll('.premium-accordion__item.is-open').forEach(i => i.classList.remove('is-open'));
      if (!isOpen) item.classList.add('is-open');
    });
  });

  /* ── Anchor Nav active state ── */
  const navLinks = document.querySelectorAll('.premium-anchor-nav__link');
  const anchorSections = document.querySelectorAll('[data-premium-anchor]');

  if (navLinks.length && anchorSections.length) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            navLinks.forEach(link => {
              link.classList.toggle('is-active', link.getAttribute('href') === '#' + entry.target.id);
            });
          }
        });
      },
      { rootMargin: '-40% 0px -55% 0px', threshold: 0 }
    );
    anchorSections.forEach(s => observer.observe(s));
  }

  /* ── Smooth scroll for anchor nav ── */
  navLinks.forEach(link => {
    link.addEventListener('click', (e) => {
      e.preventDefault();
      const target = document.querySelector(link.getAttribute('href'));
      if (target) {
        const navHeight = document.querySelector('.premium-anchor-nav')?.offsetHeight || 56;
        const top = target.getBoundingClientRect().top + window.scrollY - navHeight - 16;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    });
  });
});

/* ── Add-on "Add to Cart" ── */
class PremiumAddon extends HTMLElement {
  connectedCallback() {
    this.btn = this.querySelector('.premium-addon__btn');
    if (!this.btn) return;
    this.btn.addEventListener('click', () => this.addToCart());
  }

  async addToCart() {
    const variantId = this.dataset.variantId;
    if (!variantId) return;

    this.btn.disabled = true;
    this.btn.textContent = '...';

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      });

      if (res.ok) {
        this.btn.textContent = '✓ Added';
        this.btn.classList.add('is-added');
        fetch('/cart.js').then(r => r.json()).then(cart => {
          document.dispatchEvent(new CustomEvent('cart:update', { detail: { cart }, bubbles: true }));
          const cartCount = document.querySelector('.cart-count-bubble');
          if (cartCount) {
            cartCount.querySelectorAll('[aria-hidden]').forEach(el => el.textContent = cart.item_count);
          }
        });
      } else {
        this.btn.textContent = 'Error — retry';
        this.btn.disabled = false;
      }
    } catch {
      this.btn.textContent = 'Error — retry';
      this.btn.disabled = false;
    }
  }
}

customElements.define('premium-addon', PremiumAddon);

/* ── Variant selection sync: gallery image, hidden id input, ATC state ──
   Dawn's own variant-selects (assets/global.js) publishes
   PUB_SUB_EVENTS.optionValueSelectionChange on every option change; this
   listens for that real event instead of a made-up one. */
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-premium-variant-json]').forEach((script) => {
    const root = script.closest('.premium-product');
    if (!root) return;
    try {
      root._premiumVariantData = JSON.parse(script.textContent);
    } catch (e) {
      console.error('Premium product: could not parse variant data', e);
    }
  });

  if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

  function resolveVariant(data, selectedOptionValueIds) {
    const optionNames = [];
    selectedOptionValueIds.forEach((id) => {
      const info = data.optionValues[id];
      if (info) optionNames[info.position - 1] = info.name;
    });

    return (
      data.variants.find((v) => {
        return (
          (optionNames[0] === undefined || v.option1 === optionNames[0]) &&
          (optionNames[1] === undefined || v.option2 === optionNames[1]) &&
          (optionNames[2] === undefined || v.option3 === optionNames[2])
        );
      }) || null
    );
  }

  subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, ({ data }) => {
    const { event, selectedOptionValues } = data;
    const root = event.target.closest('.premium-product');
    if (!root || !root._premiumVariantData) return;

    const variant = resolveVariant(root._premiumVariantData, selectedOptionValues);
    if (!variant) return;

    if (variant.featuredMediaId) {
      root.querySelector('premium-gallery')?.setActive(String(variant.featuredMediaId));
    }

    const idInput = root.querySelector('product-form input[name="id"]');
    if (idInput) {
      idInput.value = variant.id;
      idInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const submitBtn = root.querySelector('.product-form__submit');
    if (submitBtn) {
      submitBtn.toggleAttribute('disabled', !variant.available);
      const span = submitBtn.querySelector('span');
      if (span) {
        span.textContent = variant.available
          ? (submitBtn.dataset.addToCartText || 'Add to cart')
          : (submitBtn.dataset.soldOutText || 'Sold out');
      }
    }
  });
});
